import { createMarketplaceClient } from '@lib/api/usg/marketplace';
import { getLandingPageSlugs } from '@lib/util/landingPageSlugs';
import {
  getProductIdFromSlug,
  getProductSlugFromPath,
} from '@lib/util/productPath';
import { getWithoutLocale } from 'utils';

export enum CompassUIPageRoutes {
  SEARCH = '/search',
  SEARCH_V2 = '/search-v2',
  LISTING = '/listing',
  USERS = '/users',
  LANDING = '/landing/',
  HOMEPAGE = '/homepage',
  PRODUCT = '/product',
  PUBLISHER = '/publisher',
  ACCOUNT_VOUCHER = '/account/voucher',
  ACCESS_DENIED_JV_CHINA = '/restricted-region',
}

/**
 * Used to determine if a page is a Compass-UI page, or a legacy asv2 page.
 * When new Compass UI pages are added, they need to be added to the CompassUIPageRoutes enum.
 */
export const isCompassUIPage = async (pathname: string): Promise<boolean> => {
  const pathnameNoLocale = getWithoutLocale(pathname) || '/';
  const isBuiltInRoute =
    pathnameNoLocale === '/' ||
    Object.values(CompassUIPageRoutes).some((route) =>
      pathnameNoLocale.startsWith(route)
    );

  if (isBuiltInRoute) {
    return true;
  }

  try {
    const slugs = await getLandingPageSlugs();
    return Boolean(slugs && slugs[pathnameNoLocale]);
  } catch {
    // Fail open to the asv2 proxy; localize-routes awaits this unguarded.
    return false;
  }
};

const isNumericalProductId = (productId: string) => /^\d+$/.test(productId);

const isAlreadyOnRoute = (route: string, pathSegments: string[]) =>
  route === `/${pathSegments.join('/')}`;

export const resolveProductRedirect = async (
  pathname: string
): Promise<{ redirect: boolean; route: string }> => {
  const pathSegments = pathname.split('/').filter(Boolean);

  if (pathSegments[0] !== 'packages' || pathSegments.length < 3) {
    return { redirect: false, route: '' };
  }

  const productId = getProductIdFromSlug(getProductSlugFromPath(pathSegments));

  if (!isNumericalProductId(productId)) {
    return { redirect: false, route: '' };
  }

  const marketplaceClient = createMarketplaceClient();

  try {
    const { category, slug, id, canonicalId } =
      await marketplaceClient.getProduct({
        productId,
      });

    const targetId = canonicalId ?? id;
    const route = category
      ? `/packages/${category.slug}/${slug}-${targetId}`
      : `/packages/${slug}-${targetId}`;

    return {
      redirect: !isAlreadyOnRoute(route, pathSegments),
      route,
    };
  } catch (error) {
    console.error(`Error fetching product with id ${productId}:`, error);
    return { redirect: false, route: '' };
  }
};
